/* eslint-disable dot-notation */
import { AlterationEntity, FerryTripEntity, PassengerTypeEntity } from 'Models/Entities';
import { BookingSummaryPassengerField, PassengerField, PassengerTypeKey } from 'Models/Enums';
import { Model } from 'Models/Model';
import { fetchPassengerTypes } from 'Util/_HumanWritten/PassengerTypeStoreUtils';
import { upperCaseFirst } from 'Util/StringUtils';
import { isNotNullOrUndefined, isNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';
import { FerryBookingCreationDto } from 'Models/Dtos/BookingDtos';
import { BookingSummary } from 'Views/Components/_HumanWritten/BookingSummaryDto';
import {
	BookingWizardData,
	getPassengerTickets,
} from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BaseBookingWizardData';
import { PassengerDetail } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/PassengerDetail';
import {
	BookingFormState,
	IBookingFormState,
} from 'Views/Components/_HumanWritten/CheckIn/FerryCheckIn/context/BookingFormState';
import { action, computed, makeObservable, observable } from 'mobx';

/**
 * Anything holding the eight per-passenger-type ticket arrays under their wizard-data names — the
 * wizard's inner data and the check-in form state both do.
 */
export type PassengerTicketSource = Partial<Record<PassengerField, PassengerDetail[] | undefined>>;

interface IPassengerTypeStore {
	/** Enabled types only — what the UI renders. */
	passengerTypes: PassengerTypeEntity[];
	/** Every type, disabled included — what driver lookups search. */
	allPassengerTypes: PassengerTypeEntity[];
	retrieveAsync(refresh?: boolean): Promise<void>;

	// Property name
	getPriceProperty<T extends Model>(key: PassengerTypeKey, model: T): string;
	getChangeProperty(key: PassengerTypeKey): string;
	getWizardDataProperty(key: PassengerTypeKey): PassengerField;
	getBookingSummaryProperty(key: PassengerTypeKey): BookingSummaryPassengerField;
	getBookingDtoProperty(key: PassengerTypeKey): PassengerField;

	// Properties
	getSingularName(key: PassengerTypeKey, capitalise?: boolean): string;
	getPluralName(key: PassengerTypeKey, capitalise?: boolean): string;
	getDescription(key: PassengerTypeKey): string;
	getAbbreviation(key: PassengerTypeKey): string;
	getTooltip(key: PassengerTypeKey): string | undefined;
	getDisabled(key: PassengerTypeKey): boolean;
	getQuantity(key: PassengerTypeKey): number;
	getId(key: PassengerTypeKey): string | undefined;
	getDriverPassengerTypeId(source: PassengerTicketSource | null | undefined): string | null;

	// Totals
	getTotalFromBookingDto(dto: FerryBookingCreationDto | BookingFormState): number;
	getTotalFromBookingSummary(summary: BookingSummary): number;
	getTotalFromWizardData(wizardData: BookingWizardData): number;

	// Passenger info
	getPassengerInfoFromBookingSummary(summary: BookingSummary | null, shortName?: boolean): string;
	getPassengerNamesFromBookingSummary(summary: BookingSummary | null): string[];
	getPassengerInfoFromFormState(formState: IBookingFormState, shortName?: boolean): string;

	// Check-in
	checkInTableHeaders: string;
	checkInTableNullRow: string;
	getCheckInTableRow(summary?: BookingSummary): string | null;
}

export class PassengerTypeStore implements IPassengerTypeStore {
	/**
	 * These are all the passenger types in the database.
	 */
	private _passengerTypes: PassengerTypeEntity[] = [];

	/**
	 * These passenger types are not disabled and will appear throughout the application.
	 */
	private _enabledPassengerTypes: PassengerTypeEntity[] = [];

	/**
	 * True means the passenger types has been fetched, false otherwise.
	 */
	private _fetched: boolean = false;

	private _passengerTypeByKey: Map<PassengerTypeKey, PassengerTypeEntity> = new Map();

	constructor() {
		makeObservable(this);
	}

	public async retrieveAsync(refresh = false) {
		if (refresh) {
			this._fetched = false;
		}
		if (this._fetched) {
			return;
		}

		const passengerTypes = await fetchPassengerTypes();

		// Sort by PassengerTypeEntity.order
		this._passengerTypes = passengerTypes.sort((a, b) => a.order - b.order);
		this._enabledPassengerTypes = this._passengerTypes.filter(x => !x.disabled);

		for (const x of passengerTypes) {
			this._passengerTypeByKey.set(x.passengerTypeKey, x);
		}

		this._fetched = true;
	}

	/**
	 * Returns the ordered list of passenger types that are being used throughout the application.
	 *
	 * <b>Rendering list.</b> A disabled type must not get a counter, a column or a price field, so
	 * anything the user sees is built from this. Anything that *finds* data already attached to a
	 * booking wants {@link allPassengerTypes} instead — see there for why.
	 */
	public get passengerTypes() {
		return this._enabledPassengerTypes;
	}

	/**
	 * Every passenger type, disabled ones included, in configured order.
	 *
	 * <p>Disabling a passenger type hides it from new bookings. It does not reach back and rewrite
	 * the bookings that already carry passengers of that type, so a booking whose driver is a type
	 * the operator has since switched off still has a driver — and code that searched only the
	 * enabled list could not see them.</p>
	 *
	 * <p>That is not hypothetical: it is how a booking ended up with two drivers. The flag sat on a
	 * disabled type, every helper reported "no driver", and the repair path then flagged a second
	 * passenger on an enabled one. Both were saved. Nothing in the UI could clear either, because
	 * the control that clears is only rendered for a type that already holds the flag.</p>
	 *
	 * <p>So: <b>find, count and clear against this list; render against {@link passengerTypes}.</b>
	 * `getDriverPassengerTypeId` always used the full list, which is why the measurement filter and
	 * the counters could disagree about who was driving.</p>
	 */
	public get allPassengerTypes() {
		return this._passengerTypes;
	}

	/**
	 * Returns the model property name of the price for the passenger type based on the entity.
	 * Typically used for FerryTrip, Alteration, and User entities.
	 */
	public getPriceProperty<T extends Model>(key: PassengerTypeKey, model: T): string {
		const modelName = model.getModelName();

		const containsPassenger = [new AlterationEntity().getModelName(), new FerryTripEntity().getModelName()];

		if (containsPassenger.includes(modelName)) {
			switch (key) {
				case 'A':
					return 'adultPassengerPrice';
				case 'B':
					return 'childPassengerPrice';
				case 'C':
					return 'infantPassengerPrice';
			}
		}

		return `price${key}`;
	}

	/**
	 * Returns the model property name of the change for the passenger type based on the entity.
	 * Typically used for Alteration entity.
	 */
	public getChangeProperty(key: PassengerTypeKey): string {
		switch (key) {
			case 'A':
				return 'adultPassengerChange';
			case 'B':
				return 'childPassengerChange';
			case 'C':
				return 'infantPassengerChange';
			default:
				return `change${key}`;
		}
	}

	/**
	 * Returns the model property name for the given passenger type of `BookingWizardData`.
	 */
	public getWizardDataProperty(key: PassengerTypeKey): PassengerField {
		switch (key) {
			case 'A':
				return 'adultTickets';
			case 'B':
				return 'childTickets';
			case 'C':
				return 'infantTickets';
			default:
				return `passenger${key}Tickets`;
		}
	}

	/**
	 * Returns the model property name for the given passenger type of `BookingSummary`.
	 */
	public getBookingSummaryProperty(key: PassengerTypeKey): BookingSummaryPassengerField {
		switch (key) {
			case 'A':
				return 'adultPassengerTickets';
			case 'B':
				return 'childPassengerTickets';
			case 'C':
				return 'infantPassengerTickets';
			default:
				return `passenger${key}Tickets`;
		}
	}

	/**
	 * Returns the model property name for the given passenger type of `FerryBookingCreationDto`.
	 *
	 * Delegates rather than repeating the mapping. The two were byte-identical switch statements a
	 * few lines apart, and code that serves both the booking wizard and the check-in form from one
	 * implementation — `Util/_HumanWritten/DriverAssignment`, the measurement filter context —
	 * depends on them agreeing. Nothing enforced that: renaming a case on one side would have
	 * compiled, type-checked, and quietly made the driver look absent to whichever flow read through
	 * the other mapper. If the two shapes ever genuinely diverge, split this again deliberately and
	 * fix those callers at the same time.
	 */
	public getBookingDtoProperty(key: PassengerTypeKey): PassengerField {
		return this.getWizardDataProperty(key);
	}

	public getSingularName(key: PassengerTypeKey, capitalise = true): string {
		const passenger = this._passengerTypeByKey.get(key);
		const name = passenger?.singularName ?? 'Passenger';
		const value = name.toLowerCase();

		if (capitalise) {
			return upperCaseFirst(value);
		}

		return value;
	}

	public getShortName(key: PassengerTypeKey, capitalise = true): string {
		const passenger = this._passengerTypeByKey.get(key);
		const name = passenger?.abbreviation ?? 'P';
		const value = name.toLowerCase();

		if (capitalise) {
			return upperCaseFirst(value);
		}

		return value;
	}

	public getPluralName(key: PassengerTypeKey, capitalise = true): string {
		const passenger = this._passengerTypeByKey.get(key);
		const name = passenger?.pluralName ?? passenger?.singularName ?? 'Passengers';
		const value = name.toLowerCase();

		if (capitalise) {
			return upperCaseFirst(value);
		}

		return value;
	}

	public getDescription(key: PassengerTypeKey): string {
		return this._passengerTypeByKey.get(key)?.description ?? '';
	}

	public getAbbreviation(key: PassengerTypeKey): string {
		const passenger = this._passengerTypeByKey.get(key);
		if (passenger && stringNotEmpty(passenger.abbreviation)) {
			return passenger.abbreviation;
		}
		return key;
	}

	public getTooltip(key: PassengerTypeKey): string | undefined {
		const passenger = this._passengerTypeByKey.get(key);
		if (passenger?.tooltipEnabled) {
			return passenger?.tooltipInfo;
		}
		return undefined;
	}

	public getDisabled(key: PassengerTypeKey): boolean {
		return this._passengerTypeByKey.get(key)?.disabled ?? true;
	}

	public getQuantity(key: PassengerTypeKey): number {
		return this._passengerTypeByKey.get(key)?.quantity ?? 1;
	}

	public getCanBeDriver(key: PassengerTypeKey): boolean {
		return this._passengerTypeByKey.get(key)?.canBeDriver ?? false;
	}

	public getId(key: PassengerTypeKey): string | undefined {
		return this._passengerTypeByKey.get(key)?.id;
	}

	/**
	 * The PassengerTypeEntity id of the passenger marked as the driver, or null when the booking has
	 * no driver yet. Works off the same `PassengerDetail.driver` flag the passenger counters set, and
	 * reads every passenger type rather than assuming the driver is an adult — `canBeDriver` is
	 * configurable per type, which is the whole point of filtering measurements by it.
	 *
	 * Takes the ticket arrays directly so the one implementation serves both the wizard's inner data
	 * and the check-in form state, which hold the same eight fields under the same names.
	 */
	public getDriverPassengerTypeId(source: PassengerTicketSource | null | undefined): string | null {
		if (!source) {
			return null;
		}
		for (const passengerType of this._passengerTypes) {
			const key = passengerType.passengerTypeKey;
			const tickets = source[this.getWizardDataProperty(key)];
			if (tickets?.some(ticket => ticket?.driver === true)) {
				return passengerType.id;
			}
		}
		return null;
	}

	/**
	 * Returns sum of passengers in data model (only include for passenger types that are not disabled).
	 */
	public getTotalFromBookingDto(dto: FerryBookingCreationDto | BookingFormState | IBookingFormState): number {
		let total = 0;
		for (const passengerType of this.passengerTypes) {
			const propertyName = this.getBookingDtoProperty(passengerType.passengerTypeKey);
			const units = dto[propertyName]?.length ?? 0;
			const passengerCount = units * this.getQuantity(passengerType.passengerTypeKey);
			total += passengerCount;
		}
		return total;
	}

	/**
	 * Returns sum of passengers in data model (only include for passenger types that are not disabled).
	 */
	public getTotalFromBookingSummary(summary: BookingSummary): number {
		let total = 0;
		for (const passengerType of this.passengerTypes) {
			const propertyName = this.getBookingSummaryProperty(passengerType.passengerTypeKey);
			const units = summary[propertyName]?.length ?? 0;
			const passengerCount = units * this.getQuantity(passengerType.passengerTypeKey);
			total += passengerCount / (passengerType.quantity ?? 1);
		}
		return total;
	}

	/**
	 * Returns sum of passengers in data model (only include for passenger types that are not disabled).
	 */
	public getTotalFromWizardData(wizardData: BookingWizardData): number {
		let total = 0;
		for (const passengerType of this.passengerTypes) {
			const propertyName = this.getWizardDataProperty(passengerType.passengerTypeKey);
			const passengerCount = getPassengerTickets(wizardData.innerWizardData, propertyName)?.length ?? 0;
			total += passengerCount;
		}
		return total;
	}

	/**
	 * Returns e.g. '1 adult, 2 children, 1 infant'.
	 */
	public getPassengerInfoFromBookingSummary(summary: BookingSummary | null, shortName: boolean = false) {
		// `IBookingFormState` used to be accepted here too, but this method resolves
		// `BookingSummary`'s property names (`adultPassengerTickets`, …), which a form state does
		// not have — a form state would have silently produced ''. Callers with a form state use
		// `getPassengerInfoFromFormState`, and `IPassengerTypeStore` already declared it this way.
		if (isNullOrUndefined(summary)) {
			return '';
		}
		const items: string[] = [];

		for (const passengerType of this.passengerTypes) {
			const key = passengerType.passengerTypeKey;
			const property = this.getBookingSummaryProperty(key);
			const tickets: PassengerDetail[] = summary[property] ?? [];
			const quantity = tickets.length / this.getQuantity(key);

			const includesDriver = tickets?.some(p => p.driver);

			if (isNotNullOrUndefined(tickets) && quantity > 0) {
				// Only include passengers with a count
				let label = quantity === 1 ? this.getSingularName(key, false) : this.getPluralName(key, false);
				let driverLabel = '';

				if (includesDriver) {
					driverLabel = ' (includes driver)';
				}
				if (shortName) {
					label = this.getShortName(key);
					items.push(`${quantity}${label}${driverLabel}`);
				} else {
					items.push(`${quantity} ${label}${driverLabel}`);
				}
			}
		}

		return items.join(', ');
	}

	/**
	 * Returns the individual passenger names captured on a booking summary (across all passenger
	 * types), skipping any ticket with no name entered. Used by the check-in confirmation modal
	 * to list who is travelling when passenger info is enabled.
	 */
	public getPassengerNamesFromBookingSummary(summary: BookingSummary | null): string[] {
		if (isNullOrUndefined(summary)) {
			return [];
		}
		const names: string[] = [];
		for (const passengerType of this.passengerTypes) {
			const property = this.getBookingSummaryProperty(passengerType.passengerTypeKey);
			const tickets: PassengerDetail[] = summary[property] ?? [];
			tickets.forEach(p => {
				const name = [p.firstName, p.lastName].filter(stringNotEmpty).join(' ');
				if (stringNotEmpty(name)) {
					names.push(name);
				}
			});
		}
		return names;
	}

	public getPassengerInfoFromFormState(formState: IBookingFormState, shortName: boolean = false) {
		if (isNullOrUndefined(formState)) {
			return '';
		}
		const items: string[] = [];

		if (formState.adultTickets?.length !== 0) {
			let label =
				formState.adultTickets?.length === 1
					? this.getSingularName('A', false)
					: this.getPluralName('A', false);
			if (shortName) {
				label = this.getShortName('A');
				items.push(`${formState.adultTickets?.length}${label}`);
			} else {
				items.push(`${formState.adultTickets?.length} ${label}`);
			}
		}
		if (formState.childTickets?.length !== 0) {
			let label =
				formState.childTickets?.length === 1
					? this.getSingularName('B', false)
					: this.getPluralName('B', false);
			if (shortName) {
				label = this.getShortName('B');
				items.push(`${formState.childTickets?.length}${label}`);
			} else {
				items.push(`${formState.childTickets?.length} ${label}`);
			}
		}
		if (formState.infantTickets?.length !== 0) {
			let label =
				formState.infantTickets?.length === 1
					? this.getSingularName('C', false)
					: this.getPluralName('C', false);
			if (shortName) {
				label = this.getShortName('C');
				items.push(`${formState.infantTickets?.length}${label}`);
			} else {
				items.push(`${formState.infantTickets?.length} ${label}`);
			}
		}
		if (formState.passengerDTickets?.length !== 0) {
			let label =
				formState.passengerDTickets?.length === 1
					? this.getSingularName('D', false)
					: this.getPluralName('D', false);
			if (shortName) {
				label = this.getShortName('D');
				items.push(`${formState.passengerDTickets?.length}${label}`);
			} else {
				items.push(`${formState.passengerDTickets?.length} ${label}`);
			}
		}
		if (formState.passengerETickets?.length !== 0) {
			let label =
				formState.passengerETickets?.length === 1
					? this.getSingularName('E', false)
					: this.getPluralName('E', false);
			if (shortName) {
				label = this.getShortName('E');
				items.push(`${formState.passengerETickets?.length}${label}`);
			} else {
				items.push(`${formState.passengerETickets?.length} ${label}`);
			}
		}
		if (formState.passengerFTickets?.length !== 0) {
			let label =
				formState.passengerFTickets?.length === 1
					? this.getSingularName('F', false)
					: this.getPluralName('F', false);
			if (shortName) {
				label = this.getShortName('F');
				items.push(`${formState.passengerFTickets?.length}${label}`);
			} else {
				items.push(`${formState.passengerFTickets?.length} ${label}`);
			}
		}
		if (formState.passengerGTickets?.length !== 0) {
			let label =
				formState.passengerGTickets?.length === 1
					? this.getSingularName('G', false)
					: this.getPluralName('G', false);
			if (shortName) {
				label = this.getShortName('G');
				items.push(`${formState.passengerGTickets?.length}${label}`);
			} else {
				items.push(`${formState.passengerGTickets?.length} ${label}`);
			}
		}
		return items.join(', ');
	}

	/**
	 * Returns e.g. "A | C | I"
	 */
	public get checkInTableHeaders() {
		const headers: string[] = [];
		for (const passenger of this.passengerTypes) {
			headers.push(this.getAbbreviation(passenger.passengerTypeKey));
		}
		return headers.join(' | ');
	}

	/**
	 * Returns e.g. "- | - | -"
	 */
	public get checkInTableNullRow() {
		return new Array(this.passengerTypes.length).fill('-').join(' | ');
	}

	/**
	 * Returns e.g. "1 | - | 3"
	 */
	public getCheckInTableRow(summary?: BookingSummary) {
		if (!summary) {
			return null;
		}

		const values: string[] = [];
		for (const passenger of this.passengerTypes) {
			const propertyName = this.getBookingSummaryProperty(passenger.passengerTypeKey);
			const quantity = passenger.quantity ?? 1;
			const value = summary[propertyName]?.length as number | undefined;
			if (value) {
				const valueToDisplay = value / quantity;
				values.push(valueToDisplay.toString());
			} else {
				// Use '-' as placeholder when value is undefined or 0
				values.push('-');
			}
		}
		return values.join(' | ');
	}
}

const passengerTypeStore = new PassengerTypeStore();
export default passengerTypeStore;
